import { BRIDGE_CAPABILITIES, type BucketProfile } from '@oysterjs/smartfill-contracts';
import { useEffect, useState } from 'react';
import type { SmartFillBridgeClient } from '../bridge/client';
import { useSidebarSession } from '../bridge/session';
import { useLearningSession } from './learning';

const BUCKET_PROFILE_CHOICES_KEY = 'cara-smart-fill-bucket-profiles-v1';

// One load per bridge connection, since profiles change only with a deploy.
const profileLoads = new WeakMap<SmartFillBridgeClient, Promise<BucketProfile[]>>();

/**
 * The bucket profiles. Empty while they load, when the extension predates
 * them, when they fail to load, and while site profiles are off for the user.
 */
export function useBucketProfiles(): BucketProfile[] {
  const { client } = useSidebarSession();
  const { capabilities } = useLearningSession();
  const supported = capabilities.includes(BRIDGE_CAPABILITIES.BUCKET_PROFILES);
  const [profiles, setProfiles] = useState<BucketProfile[]>([]);

  useEffect(() => {
    if (!supported) return;
    let load = profileLoads.get(client);
    if (!load) {
      load = client.request('profiles.list', {});
      profileLoads.set(client, load);
      // A failed load is retried by the next view that asks.
      void load.catch(() => profileLoads.delete(client));
    }
    let live = true;
    void load.then(
      (next) => {
        if (live) setProfiles(next);
      },
      () => undefined
    );
    return () => {
      live = false;
    };
  }, [client, supported]);

  return supported ? profiles : [];
}

/**
 * The profile a person chose for a bucket on this device, or null when they
 * have not chosen and the panel infers one from the bucket's facts.
 */
export function rememberedBucketProfile(bucketId: string): string | null {
  return readChoices()[bucketId] ?? null;
}

export function rememberBucketProfile(bucketId: string, profileId: string | null): void {
  const choices = readChoices();
  if (profileId === null) delete choices[bucketId];
  else choices[bucketId] = profileId;
  try {
    window.localStorage.setItem(BUCKET_PROFILE_CHOICES_KEY, JSON.stringify(choices));
  } catch {
    // The pattern falls back to inferring a profile when storage is unavailable.
  }
}

function readChoices(): Record<string, string> {
  try {
    const stored = window.localStorage.getItem(BUCKET_PROFILE_CHOICES_KEY);
    const parsed: unknown = stored ? JSON.parse(stored) : {};
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    return Object.fromEntries(
      Object.entries(parsed).filter(
        (entry): entry is [string, string] => typeof entry[1] === 'string'
      )
    );
  } catch {
    return {};
  }
}
